import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { PaginationProps } from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { AurDropdownButton } from '../aur-button/AurDropdownButton';
import { ReactComponent as DropDownIcon } from '../../../../src/assests/svg/drop_down.svg';
import AurButton from '../aur-button/AurButton';
import { Divider } from '@mui/material';
import AurSvgIcon from '../aur-svg-icon/AurSvgIcon';
import { t } from 'i18next';
import { AurTextField } from '../aur-text-field/AurTextField';
import { formateNumber } from '../../../utils/utils';
import styles from '../aur-select-dropdown/style.module.scss';
import AurIcon, { IconColors, IconNames } from '../aur-icon/AurIcon';
import AurTooltip from '../aur-tooltip/AurTooltip';
import { useAnalytics } from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';

// Define the props for the AurPaginationPro component, extending from PaginationProps
type AurPaginationProProps = PaginationProps & {
  totalItems: number; // Total number of items to paginate through
  itemsPerPage: number; // Number of items per page
  startIndex: number; // start index of active page
  onPageChange: (page: number) => void; // Function to handle page change
  setItemsPerPage: (itemsPerPage: number) => void; // Function to set items per page
  perPageOptions?: number[]; // Optional array of items per page options
  backgroundColor?: string; // Optional background color for the pagination box
  selectedDocs?: number;
  isFilterApplied?: boolean;
  onClearFilter?: () => void;
  handleAddSortFieldClick?: () => void; // Function to handle add sort field
  handleTagClick?: React.MouseEventHandler<HTMLButtonElement>; // Function to handle tag click
  handleDownloadClick?: () => void; // Function to handle download click
  handleViewClick?: () => void; // Function to handle download click
  selectionText?: string; // Use custom selected count text
  showViewButton?: boolean;
  showTagButton?: boolean;
  trackPaginationActionName?: string;
};

// AurPaginationPro functional component definition
const AurPaginationPro: React.FC<AurPaginationProProps> = ({
  totalItems,
  itemsPerPage,
  startIndex,
  onPageChange,
  setItemsPerPage,
  perPageOptions = [50, 100, 200, 500, 1000],
  backgroundColor = '#121A25',
  selectedDocs = 0,
  isFilterApplied = false,
  onClearFilter,
  handleAddSortFieldClick,
  handleTagClick,
  handleDownloadClick,
  handleViewClick,
  selectionText = '',
  showViewButton = false,
  showTagButton = false,
  trackPaginationActionName = t('gaTracking.global.reportPagination'),
}) => {
  const [searchIndex, setSearchIndex] = useState<number | string>(startIndex);
  const [hideButtonLabel, setHideButtonLabel] = useState(false);
  const { trackGAEvent } = useAnalytics();

  const MEDIUM_SCREEN = 848;
  const SMALL_SCREEN = 638;

  const trackPagination = (
    name: string,
    type: string = GA_CLICK_TYPES.LINK,
    region: string = GA_REGIONS.HEADER
  ) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: name,
        type: type,
        region: region,
        action: trackPaginationActionName,
      },
    });
  };

  useEffect(() => {
    setSearchIndex(formateNumber(startIndex, { useGrouping: false }));
  }, [startIndex]);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (
        window.innerWidth > SMALL_SCREEN &&
        window.innerWidth <= MEDIUM_SCREEN &&
        selectedDocs.toString().length > 3
      ) {
        setHideButtonLabel(true);
      } else {
        setHideButtonLabel(false);
      }
    });
    observer.observe(document.body);

    return () => {
      observer.disconnect();
    };
  }, [selectedDocs]);

  // Calculate whether we are on the last page
  const isAtLastPage = (startIndex * itemsPerPage) > totalItems;

  // Function to handle the click event for previous or next page buttons
  const handlePageClick = (isPrevious: boolean) => {
    let currentPageIndex = startIndex;

    if (!isPrevious && currentPageIndex >= 1) {
      currentPageIndex += 1
    } else if (isPrevious && currentPageIndex > 1) {
      currentPageIndex -= 1
    }

    if (isPrevious) {
      trackPagination(
        t('gaTracking.pagination.leftArrow'),
        GA_CLICK_TYPES.LINK,
        GA_REGIONS.HEADER
      );
    } else {
      trackPagination(
        t('gaTracking.pagination.rightArrow'),
        GA_CLICK_TYPES.LINK,
        GA_REGIONS.HEADER
      );
    }
    onPageChange(currentPageIndex);
  };

  const handleFirstAndLastPageClick = (isFirst: boolean) => {
    let startIndex = 1;
    if (!isFirst) {
      startIndex = Math.ceil(totalItems / itemsPerPage);
    }
    if (isFirst) {
      trackPagination(
        t('gaTracking.pagination.firstPage'),
        GA_CLICK_TYPES.LINK,
        GA_REGIONS.HEADER
      );
    } else {
      trackPagination(
        t('gaTracking.pagination.lastPage'),
        GA_CLICK_TYPES.LINK,
        GA_REGIONS.HEADER
      );
    }
    onPageChange(startIndex);
  };

  // Handle input changes
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (/^\d*$/.test(inputValue.replace(/,/g, ''))) {
      let formattedValue = inputValue.replace(/,/g, '');
      formattedValue = Number(formattedValue).toLocaleString();
      // Trim leading zeros
      if (formattedValue.startsWith('0') && formattedValue.length > 0) {
        formattedValue = formattedValue.replace(/^0+/, '');
      }
      trackPagination(formattedValue, GA_CLICK_TYPES.INPUT, GA_REGIONS.HEADER);
      setSearchIndex(formattedValue);
    }
  };

  // Handle search based in index
  const handleSearchOnEnterPress = (event: KeyboardEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    if (event.key === 'Enter' && target.value != '') {
      const numericValue = target.value.replace(/,/g, '');
      const parsedValue = Number(numericValue);
      if (!isNaN(parsedValue)) {
        onPageChange(parsedValue);
      }
    }
  };

  return (
    // Main container with customizable background color
    <Box
      bgcolor={backgroundColor}
      className="aurPaginationPro"
      aria-label={t('common.pagination.paginationHint')}
      aria-live="polite"
      data-testid="aurPaginationPro"
    >
      <Stack className="doc-container">
        <Typography className="typo-text body3_size custom-text-color" tabIndex={0}>
          {selectionText ||
            t('common.pagination.selectedDocs', {
              selectedDocs: formateNumber(selectedDocs),
            })}
        </Typography>
        <Box className="button-container">
          {showTagButton ? (
            <AurTooltip
              title={t('common.pagination.tagTooltip')}
              placement="top"
              className="typo-text label3_size"
              disableInteractive
              disableFocusListener
            >
              <div>
                <AurButton
                  onClick={handleTagClick}
                  disabled={selectedDocs === 0}
                  startIcon={
                    <AurIcon
                      icon={IconNames.Tag}
                      color={IconColors.Custom}
                      customClassName={
                        hideButtonLabel
                          ? 'button-icon big-button-icon'
                          : 'button-icon'
                      }
                    />
                  }
                  type="continue"
                  size="Small"
                  label={hideButtonLabel ? '' : t('common.pagination.tag')}
                  dataTestId="tag-button"
                  classNames={
                    hideButtonLabel
                      ? 'icon-with-button hide-button'
                      : 'icon-with-button'
                  }
                />
              </div>
            </AurTooltip>
          ) : null}
          {showViewButton ? (
            <AurTooltip
              title={t('common.pagination.viewTooltip')}
              placement="top"
              className="typo-text label3_size"
              disableInteractive
              disableFocusListener
            >
              <div>
                <AurButton
                  onClick={handleViewClick}
                  disabled={selectedDocs === 0}
                  startIcon={
                    <AurIcon
                      icon={IconNames.View}
                      color={IconColors.Custom}
                      customClassName={
                        hideButtonLabel
                          ? 'button-icon big-button-icon'
                          : 'button-icon'
                      }
                    />
                  }
                  type="continue"
                  size="Small"
                  label={hideButtonLabel ? '' : t('common.pagination.view')}
                  dataTestId="view-button"
                  classNames={
                    hideButtonLabel
                      ? 'icon-with-button hide-button'
                      : 'icon-with-button'
                  }
                />
              </div>
            </AurTooltip>
          ) : null}
        </Box>
      </Stack>
      <Stack direction="row" gap={2}>
        <AurDropdownButton
          buttonTitle={`${t('common.pagination.perPage')}`}
          buttonTitleClass="custom-text-color hide-label"
          buttonText={`${itemsPerPage}`}
          type="text"
          endIcon={<DropDownIcon aria-hidden="true" />}
          aria-live="polite"
          size="Small"
          iconType="Arrow"
          menuDirectionLeft={false}
          aria-haspopup="true"
          aria-expanded="false"
          aria-controls="perPageOptions"
          menuItemHeight="small"
          menuItemWidth="small"
          menuItemTextSize="small"
          classNames="pagination-dropdown-button-icon"
          hintText={`${t('common.pagination.perPage')}`}
          // Generate menu items based on perPageOptions array
          menuItems={perPageOptions.map((option) => {
            return {
              label: option.toString(),
              onClick: () => {
                setItemsPerPage(option); // Update items per page on click
                trackPagination(
                  `${option + ' ' + t('common.pagination.perPage')}`,
                  GA_CLICK_TYPES.LINK,
                  GA_REGIONS.HEADER
                );
              },
            };
          })}
          buttonProps={{ disabled: totalItems == 0 }}
        />
        <Divider orientation="vertical" flexItem />
        <Stack direction="row" spacing={1}>
          <Stack direction="row" spacing={0.25}>
            <AurTooltip
              title={t('common.pagination.first')}
              placement="top"
              className="typo-text label3_size"
              disableInteractive
              disableFocusListener
            >
              <div>
                <AurButton
                  classNames="icon-button-arrow pagination-arrow-button"
                  onClick={() => handleFirstAndLastPageClick(true)} // Navigate to first page
                  minWidth={32}
                  type="exit"
                  size="Small"
                  disabled={startIndex === 1} // Disable button if on first page
                  ariaLabel={`${t('common.pagination.first')}`}
                  aria-disabled={startIndex === 1}
                  dataTestId="first-page-button"
                >
                  <AurSvgIcon
                    src={
                      startIndex === 1
                        ? 'images/page_first.svg'
                        : 'images/page_first_filled.svg'
                    }
                    ariaHidden={true}
                  />
                </AurButton>
              </div>
            </AurTooltip>

            <AurTooltip
              title={t('common.pagination.previous')}
              placement="top"
              className="typo-text label3_size"
              disableInteractive
              disableFocusListener
            >
              <div>
                <AurButton
                  classNames="icon-button-arrow pagination-arrow-button"
                  onClick={() => handlePageClick(true)} // Navigate to previous page
                  minWidth={32}
                  type="exit"
                  size="Small"
                  disabled={startIndex === 1} // Disable button if on first page
                  ariaLabel={`${t('common.pagination.previous')}`}
                  aria-disabled={startIndex === 1}
                  dataTestId="previous-page-button"
                >
                  <AurSvgIcon
                    src={
                      startIndex === 1
                        ? 'images/Icon_left.svg'
                        : 'images/Icon_left_filled.svg'
                    }
                    ariaHidden={true}
                  />
                </AurButton>
              </div>
            </AurTooltip>
          </Stack>
          <AurTextField
            disabled={totalItems == 0}
            onChange={handleChange}
            variant="outlined"
            className={`aur-textfield pagination-textinput ${styles.aurMultiSelectSearch}`}
            value={searchIndex}
            data-testid="pagination-textfield"
            onKeyUp={handleSearchOnEnterPress}
            ariaLabel={t('common.pagination.pageNumber')}
          />
          <Stack direction="row" spacing={0.25}>
            <AurTooltip
              title={t('common.pagination.next')}
              placement="top"
              className="typo-text label3_size"
              disableInteractive
              disableFocusListener
            >
              <div>
                <AurButton
                  classNames="icon-button-arrow pagination-arrow-button"
                  onClick={() => handlePageClick(false)} // Navigate to next page
                  minWidth={32}
                  type="exit"
                  size="Small"
                  disabled={isAtLastPage} // Disable button if on last page
                  ariaLabel={`${t('common.pagination.next')}`}
                  aria-disabled={isAtLastPage}
                  dataTestId="next-page-button"
                >
                  <AurSvgIcon
                    src={
                      isAtLastPage
                        ? 'images/Icon_right.svg'
                        : 'images/Icon_right_filled.svg'
                    }
                    ariaHidden={true}
                  />
                </AurButton>
              </div>
            </AurTooltip>
            <AurTooltip
              title={t('common.pagination.last')}
              placement="top"
              className="typo-text label3_size"
              disableInteractive
              disableFocusListener
            >
              <div>
                <AurButton
                  classNames="icon-button-arrow pagination-arrow-button"
                  onClick={() => handleFirstAndLastPageClick(false)} // Navigate to last page
                  minWidth={32}
                  type="exit"
                  size="Small"
                  disabled={isAtLastPage} // Disable button if on last page
                  ariaLabel={`${t('common.pagination.last')}`}
                  aria-disabled={isAtLastPage}
                  dataTestId="last-page-button"
                >
                  <AurSvgIcon
                    src={
                      isAtLastPage
                        ? 'images/page_last.svg'
                        : 'images/page_last_filled.svg'
                    }
                    ariaHidden={true}
                  />
                </AurButton>
              </div>
            </AurTooltip>
          </Stack>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        spacing={1}
      >
        <Stack direction="row" spacing={1}>
          {isFilterApplied && (
            <AurTooltip
              title={t('common.pagination.clearFilters')}
              placement="top"
              className="typo-text label3_size"
              disableInteractive
              disableFocusListener
            >
              <div>
                <AurButton
                  classNames="icon-button"
                  onClick={onClearFilter}
                  type="exit"
                  size="Small"
                  minWidth={32}
                  ariaLabel={t('common.pagination.clearFilters')}
                  dataTestId={'clear-filters'}
                >
                  <AurSvgIcon src="images/clear_filters.svg" ariaHidden={true} />
                </AurButton>
              </div>
            </AurTooltip>
          )}
          <AurTooltip
            title={t('common.pagination.manageColumns')}
            placement="top"
            className="typo-text label3_size"
            disableInteractive
            disableFocusListener
          >
            <div>
              <AurButton
                classNames="icon-button"
                onClick={handleAddSortFieldClick}
                type="exit"
                size="Small"
                minWidth={32}
                ariaLabel={t('common.pagination.manageColumns')}
                dataTestId={'add-fields-sort'}
              >
                <AurSvgIcon src="images/add_fields_sort.svg" ariaHidden={true} />
              </AurButton>
            </div>
          </AurTooltip>
        </Stack>
        <AurTooltip
          title={t('common.pagination.download')}
          placement="top"
          className="typo-text label3_size"
          disableInteractive
          disableFocusListener
        >
          <div>
            <AurButton
              classNames="icon-button"
              onClick={handleDownloadClick}
              type="exit"
              size="Small"
              minWidth={32}
              ariaLabel={t('common.pagination.download')}
              dataTestId={'download-docs'}
              startIcon={<AurSvgIcon src={'images/download.svg'} ariaHidden={true} />}
            ></AurButton>
          </div>
        </AurTooltip>
      </Stack>
    </Box>
  );
};

export default AurPaginationPro;
